import { pushEcommerceEvent } from '../analytics/ga/ecommerce';
import { NewContentLoadedEvent } from '../events/new-content-loaded';
import { parseEscapedJSONString } from '../json/parse';
import { isTouchDevice } from './device-info';

function mixedGridOptionsHandler(): void {
    const mixedGridArticleSelector = '.mixed-grid article';

    let openedViaTouchEvent = false;

    const hideOptions = ( optionsPanel: HTMLElement ): void => {
        optionsPanel.classList.remove( 'visible', 'with-close-btn' );
        openedViaTouchEvent = false;
    };

    const showOptions = ( event: Event, showCloseButton: boolean ): void => {
        if ( !event.target || !( event.target instanceof HTMLElement ) || !event.target.closest( mixedGridArticleSelector ) ) {
            return;
        }

        const parentArticle = event.target.closest( mixedGridArticleSelector );

        if ( !parentArticle ) {
            throw new Error( 'no parent article found for options panel' );
        }

        const optionsPanel = parentArticle.querySelector<HTMLElement>( '.options' );

        if ( !optionsPanel ) {
            throw new Error( 'no options panel found' );
        }

        if ( optionsPanel.classList.contains( 'visible' ) ) {
            document.querySelectorAll<HTMLElement>( '.options' ).forEach( hideOptions );
        }

        const classes = [ 'visible' ];

        if ( showCloseButton ) {
            classes.push( 'with-close-btn' );
        }

        optionsPanel.classList.add( ...classes );

        if ( window.TouchEvent && event instanceof TouchEvent === true ) {
            openedViaTouchEvent = true;
        }
    };

    document.addEventListener( 'touchstart', ( event ) => {
        let scrolled = false;

        document.addEventListener( 'scroll', () => scrolled = true, { passive: true } );

        document.addEventListener( 'touchend', () => {
            if ( scrolled || !event.target || !( event.target instanceof HTMLElement ) || !event.target.closest( mixedGridArticleSelector ) ) {
                return;
            }
            showOptions( event, isTouchDevice() );
        } );
    }, { passive: true } );

    document.addEventListener( 'click', ( event: Event ) => {
        if ( !event.target || !( event.target instanceof HTMLElement ) || !event.target.closest( mixedGridArticleSelector ) ) {
            return;
        }

        showOptions( event, isTouchDevice() );
    } );

    document.addEventListener( 'click', ( event: Event ) => {
        if ( !event.target || !( event.target instanceof HTMLElement ) || !event.target.matches( mixedGridArticleSelector + ' .do-close' ) ) {
            return;
        }

        const optionsPanel = event.target.closest<HTMLElement>( '.options' );

        if ( !optionsPanel ) {
            throw new Error( 'no options panel found on click' );
        }

        hideOptions( optionsPanel );
    } );

    document.addEventListener( 'mouseleave', ( event: MouseEvent ) => {
        if ( !event.target || !( event.target instanceof HTMLElement ) || !event.target.matches( mixedGridArticleSelector ) ) {
            return;
        }

        if ( openedViaTouchEvent ) {
            return;
        }

        const optionsPanel = event.target.querySelector<HTMLElement>( '.options' );

        if ( !optionsPanel ) {
            throw new Error( 'no options panel found on mouseleave' );
        }

        hideOptions( optionsPanel );
    }, true );
}

function loadMoreItemsHandler( options: ItemPaginationOptions ): void {
    const btn = document.querySelector<HTMLButtonElement>( '.show-more' );

    if ( !btn ) {
        return;
    }

    const form = btn.closest<HTMLFormElement>( 'form' );

    btn.addEventListener( 'click', async function( event: Event ) {
        let items = document.querySelectorAll<HTMLElement>( options.countableSelector ).length;

        if ( !items ) {
            items = 0;
        }

        let itemsToIgnore = document.querySelectorAll<HTMLElement>( options.ignoreSelector ).length;

        if ( !itemsToIgnore ) {
            itemsToIgnore = 0;
        }

        const formData = new FormData();
        formData.append( 'skip', ( items - itemsToIgnore ).toString() );
        formData.append( 'take', '12' );

        if ( form ) {
            const type = form.querySelector<HTMLInputElement>( '[name=type]' );

            if ( type ) {
                formData.append( 'type', type.value );
            }

            const section = form.querySelector<HTMLInputElement>( '[name=section]' );

            if ( section ) {
                formData.append( 'section', section.value );
            }
        }

        const response = await window.http( btn.dataset.paginationUrl as string, {
            method: 'POST',
            body: formData,
        } );

        const result = await response.json();

        // TODO - remove setting up styles with javascript after TSRWD-2175 lands
        // setting in js will fix ba bug when button has other display type that overwrites one from d-none
        // eventually d-none should be display: none !important
        if ( !result.continue ) {
            btn.style.display = 'none';
            btn.classList.add( 'd-none' );
        }

        const el = document.querySelector<HTMLElement>( '.mixed-grid' );

        if ( !el ) {
            return;
        }

        const range = document.createRange();
        range.selectNode( el );
        el.appendChild( range.createContextualFragment( result.content ) );

        document.dispatchEvent( new CustomEvent<NewContentLoadedEvent>( 'twc:newcontentloaded', {
            bubbles: true,
            detail: {
                container: document,
            },
        } ) );
    } );
}

function removeSavedItemHandler(): void {
    document.addEventListener( 'click', async ( event: Event ) => {
        if ( !event.target || !( event.target as HTMLElement ).closest( '#save_tab [data-saved-id] .saved-remove' ) ) {
            return;
        }

        event.preventDefault();

        const target = event.target as HTMLElement;

        const el = target.closest<HTMLElement>( '[data-saved-id]' );

        if ( !el ) {
            return;
        }

        const savedItemID = el.dataset.savedId;

        if ( !savedItemID ) {
            return;
        }

        const formData = new FormData();
        formData.append( 'id', savedItemID );

        const response: Response = await window.http( '/saved/remove', {
            method: 'POST',
            body: formData,
        } );

        if ( !response.ok ) {
            throw new Error( `failed to removed saved item ${savedItemID}` );
        }

        const parentColumn = el.closest( '.col-xs-12' );

        if ( parentColumn ) {
            parentColumn.remove();
        }

        if ( target.dataset.removeFromCartEvent && target.dataset.removeFromCartEvent.length > 0 ) {
            pushEcommerceEvent( parseEscapedJSONString( target.dataset.removeFromCartEvent ) );
        }

        window.dispatchEvent( new CustomEvent( 'twc:update-saved-counter', {
            bubbles: true,
            detail: {
                delta: -1,
            },
        } ) );
    } );
}

type ItemPaginationOptions = {
    countableSelector: string;
    ignoreSelector: string;
}

type ItemGridOptions = {
    paginationOptions?: ItemPaginationOptions;
}

export default function initUserItemsGrid( options: ItemGridOptions = {} ): void {
    removeSavedItemHandler();

    mixedGridOptionsHandler();

    if ( options.paginationOptions ) {
        loadMoreItemsHandler( options.paginationOptions );
    }
}
